import React, { useState } from "react";
import { Wrench, User, Package, Calendar, ChevronRight, CheckCircle, Clock, Check, Phone, MessageSquare, X } from "lucide-react";
import { ComponentProps, Case } from "../types/types";
import { supabaseService } from "../SupabaseService";

const formatPhoneNumber = (phone: string) => {
  if (!phone || phone.length !== 8) return phone;
  return `${phone.slice(0, 3)} ${phone.slice(3, 5)} ${phone.slice(5, 8)}`;
};

const Service: React.FC<ComponentProps> = ({
  cases,
  setCases,
  customers,
  openCaseForEditing,
}) => {
  const [isMoving, setIsMoving] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [isUpdating, setIsUpdating] = useState<number | null>(null);
  const [showSmsDialog, setShowSmsDialog] = useState(false);
  const [selectedCaseForMove, setSelectedCaseForMove] = useState<Case | null>(null);

  const serviceCases = cases.filter((case_) => case_.kategori === "Service");
  
  // Identifiser langtidsordre
  const langtidsOrdre = serviceCases.filter(case_ => 
    case_.kommentar?.toLowerCase().includes("langtid") || 
    case_.kommentar?.toLowerCase().includes("venter på deler") ||
    case_.kommentar?.toLowerCase().includes("langvented")
  );
  
  // Aktive serviceordre (uten langtidsordre)
  const aktiveServiceCases = serviceCases.filter(case_ => 
    !langtidsOrdre.some(l => l.id === case_.id)
  );
  
  // Filtrer basert på søk - kun aktive ordre
  const filteredCases = aktiveServiceCases.filter(
    (case_) =>
      case_.kunde.toLowerCase().includes(searchTerm.toLowerCase()) ||
      case_.maskinMerke?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      case_.beskrivelse.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // SORTER: Service ferdig først (grønne), deretter etter opprettet dato
  const sortedCases = [...filteredCases].sort((a, b) => {
    // Sjekk om service er ferdig
    const aReady = !!(a.serviceRepUtført && a.utfortDato);
    const bReady = !!(b.serviceRepUtført && b.utfortDato);
    
    // Hvis en er ferdig og en ikke, vis ferdig først
    if (aReady && !bReady) return -1;
    if (!aReady && bReady) return 1;
    
    // Ellers sorter etter dato (eldste først)
    const dateA = new Date(a.opprettet || a.dato).getTime();
    const dateB = new Date(b.opprettet || b.dato).getTime();
    return dateA - dateB;
  });

  // Grupper ordre etter kunde
  const groupedCases = sortedCases.reduce((groups, case_) => {
    const customerName = case_.kunde;
    if (!groups[customerName]) {
      groups[customerName] = [];
    }
    groups[customerName].push(case_);
    return groups;
  }, {} as Record<string, typeof sortedCases>);

  // Flytt ordre til langtidsordre
  const moveToLangtidsordre = async (caseId: number) => {
    const caseToUpdate = cases.find(c => c.id === caseId);
    if (!caseToUpdate) return;

    const updatedCase = {
      ...caseToUpdate,
      kommentar: `${caseToUpdate.kommentar || ""} LANGTID - Venter på deler`.trim()
    };

    setCases(prev => prev.map(c => c.id === caseId ? updatedCase : c));
    await supabaseService.saveCase(updatedCase);
  };

  // Flytt ordre tilbake til aktive
  const moveBackToActive = async (caseId: number) => {
    const caseToUpdate = cases.find(c => c.id === caseId);
    if (!caseToUpdate) return;

    const updatedCase = {
      ...caseToUpdate,
      kommentar: caseToUpdate.kommentar
        ?.replace(/LANGTID.*$/gi, "")
        ?.replace(/langtid.*$/gi, "")
        ?.replace(/venter på deler.*$/gi, "")
        ?.trim() || ""
    };

    setCases(prev => prev.map(c => c.id === caseId ? updatedCase : c));
    await supabaseService.saveCase(updatedCase);
  };

  const handleMoveToKlarTilHenting = async (case_: Case) => {
    setSelectedCaseForMove(case_);
    setShowSmsDialog(true);
  };

  const confirmMoveToKlarTilHenting = async () => {
    if (!selectedCaseForMove) return;
    
    setIsMoving(selectedCaseForMove.id);
    setShowSmsDialog(false);
    const originalCases = [...cases];
    
    try {
      const today = new Date().toISOString().split("T")[0];
      const updatedCase = {
        ...selectedCaseForMove,
        kategori: "Klar til henting",
        utfortDato: selectedCaseForMove.utfortDato || today,
        opprettet: new Date().toISOString(),
      };
      
      setCases(prev =>
        prev.map(case_ =>
          case_.id === selectedCaseForMove.id ? updatedCase : case_
        )
      );
      
      const saved = await supabaseService.saveCase(updatedCase);
      if (!saved) {
        throw new Error("Kunne ikke lagre endring");
      }
      
      console.log(`✅ Flyttet sak ${selectedCaseForMove.id} til Klar til henting`);
    } catch (error) {
      setCases(originalCases);
      console.error("Feil ved flytting av sak:", error);
      alert("❌ Kunne ikke flytte ordre. Prøv igjen.");
    } finally {
      setIsMoving(null);
      setSelectedCaseForMove(null);
    }
  };

  const handleServiceCompleted = async (caseId: number, completed: boolean) => {
    setIsUpdating(caseId);
    const originalCases = [...cases];
    
    try {
      const caseToUpdate = cases.find(c => c.id === caseId);
      if (!caseToUpdate) throw new Error("Sak ikke funnet");
      
      const today = new Date().toISOString().split("T")[0];
      const userName = localStorage.getItem('hagia-user-name') || 'Ukjent';
      
      const updatedCase = {
        ...caseToUpdate,
        serviceRepUtført: completed ? userName : "",
        utfortDato: completed ? today : "",
      };
      
      setCases(prev =>
        prev.map(case_ =>
          case_.id === caseId ? updatedCase : case_
        )
      );
      
      const saved = await supabaseService.saveCase(updatedCase);
      if (!saved) {
        throw new Error("Kunne ikke lagre endring");
      }
      
      console.log(`✅ Service ${completed ? 'ferdig' : 'ikke ferdig'} for sak ${caseId}`);
    } catch (error) {
      setCases(originalCases);
      console.error("Feil ved oppdatering av service status:", error);
    } finally {
      setIsUpdating(null);
    }
  };

  const getDaysInService = (case_: Case) => {
    const startDate = new Date(case_.opprettet || case_.dato);
    const now = new Date();
    return Math.floor((now.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
  };

  const getServiceStatusBadge = (case_: Case) => {
    const days = getDaysInService(case_);
    const isReady = case_.serviceRepUtført && case_.utfortDato;
    
    if (isReady) {
      return (
        <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-700 font-medium flex items-center space-x-1">
          <CheckCircle size={12} />
          <span>Service ferdig</span>
        </span>
      );
    }
    
    if (days > 7) {
      return (
        <span className="px-2 py-1 text-xs rounded-full bg-red-100 text-red-700 font-medium flex items-center space-x-1">
          <Clock size={12} />
          <span>{days} dager</span>
        </span>
      );
    }
    
    if (days > 3) {
      return (
        <span className="px-2 py-1 text-xs rounded-full bg-yellow-100 text-yellow-700 font-medium flex items-center space-x-1">
          <Clock size={12} />
          <span>{days} dager</span>
        </span>
      );
    }
    
    return (
      <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-600 font-medium flex items-center space-x-1">
        <Clock size={12} />
        <span>{days} {days === 1 ? 'dag' : 'dager'}</span>
      </span>
    );
  };

  const editCase = (case_: Case) => {
    openCaseForEditing(case_);
  };

  return (
    <div className="grid grid-cols-6 gap-4">
      {/* HOVEDKOLONNE - AKTIVE SERVICEORDRE */}
      <div className="col-span-5">
        <div className="bg-white rounded-lg shadow-sm border">
          <div className="p-4 border-b bg-green-50">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Wrench className="text-green-600" size={24} />
                <h2 className="text-xl font-semibold text-gray-900">Service</h2>
                <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">
                  {aktiveServiceCases.length} {aktiveServiceCases.length === 1 ? 'sak' : 'saker'}
                </span>
              </div>
              <div className="text-sm text-green-700">
                {aktiveServiceCases.filter(c => c.serviceRepUtført && c.utfortDato).length} ferdig
              </div>
            </div>
          </div>

          <div className="p-4">
            <input
              type="text"
              placeholder="Søk i service-saker..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent mb-4"
            />

            {sortedCases.length === 0 ? (
              <div className="text-center py-12">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-4">
                  <Wrench className="text-gray-400" size={32} />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  {searchTerm ? "Ingen treff" : "Ingen saker til service"}
                </h3>
                <p className="text-gray-600">
                  {searchTerm ? "Prøv et annet søkeord" : "Saker under reparasjon vil vises her"}
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {Object.entries(groupedCases).map(([customerName, customerCases]) => {
                  // Hvis kunden kun har én ordre, vis som vanlig enkeltordre
                  if (customerCases.length === 1) {
                    const case_ = customerCases[0];
                    const isReady = case_.serviceRepUtført && case_.utfortDato;
                    const days = getDaysInService(case_);
                    
                    return (
                      <div
                        key={case_.id}
                        className={`p-3 rounded-lg transition-all border-2 ${
                          isReady 
                            ? "border-green-300 bg-green-50" 
                            : days > 7
                            ? "border-red-300 bg-red-50"
                            : days > 3
                            ? "border-yellow-300 bg-yellow-50"
                            : "border-gray-200 bg-white"
                        }`}
                      >
                        {/* ØVERSTE LINJE: Kunde og ferdig/dato checkbox ØVERST TIL HØYRE */}
                        <div className="flex items-start justify-between mb-2">
                          <div 
                            className="flex-1 cursor-pointer"
                            onClick={() => editCase(case_)}
                          >
                            <div className="flex items-center space-x-2">
                              <User className="text-gray-500" size={18} />
                              <h3 className="text-base font-semibold text-gray-900">
                                {case_.kunde}
                              </h3>
                              <span className="text-xs text-gray-500">
                                #{case_.id}
                              </span>
                            </div>
                          </div>
                          
                          {/* FERDIG CHECKBOX OG STATUS ØVERST TIL HØYRE */}
                          <div className="flex items-center space-x-3">
                            {getServiceStatusBadge(case_)}
                            <label className="flex items-center space-x-1 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={isReady}
                                onChange={(e) => handleServiceCompleted(case_.id, e.target.checked)}
                                disabled={isUpdating === case_.id}
                                className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                              />
                              <span className="text-sm text-gray-700">Ferdig</span>
                            </label>
                          </div>
                        </div>

                        <div 
                          className="cursor-pointer"
                          onClick={() => editCase(case_)}
                        >
                          <div className="space-y-1 mb-2">
                            <div className="flex items-center space-x-2 text-sm text-gray-700">
                              <Package size={14} className="text-gray-400" />
                              <span className="font-medium">{case_.maskinMerke || "Ikke spesifisert"}</span>
                              {case_.serienr && (
                                <span className="text-gray-500">- {case_.serienr}</span>
                              )}
                            </div>
                            <div className="text-sm text-gray-600 line-clamp-1">
                              {case_.beskrivelse}
                            </div>
                          </div>
                          
                          {case_.kommentar && (
                            <div className="text-xs text-gray-600 mt-2 p-2 bg-blue-50 border border-blue-200 rounded">
                            <strong>Kommentar:</strong> {case_.kommentar}
                            </div>
                          )}

                          {isReady && (
                            <div className="text-xs text-green-700 bg-green-100 rounded px-2 py-1 inline-block mb-2">
                              ✅ Utført av {case_.serviceRepUtført} - {case_.utfortDato}
                            </div>
                          )}
                        </div>

                        {/* NEDERSTE LINJE: Klar til henting knapp til venstre, Langtid knapp NEDERST TIL HØYRE */}
                        <div className="flex items-center justify-between mt-3">
                          {/* Klar til henting knapp - kun hvis ferdig */}
                          <div className="flex-1">
                            {isReady && (
                              <button
                                onClick={() => handleMoveToKlarTilHenting(case_)}
                                disabled={isMoving === case_.id}
                                className={`flex items-center justify-center space-x-1 px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm ${
                                  isMoving === case_.id ? "opacity-50 cursor-not-allowed" : ""
                                }`}
                              >
                                <span>{isMoving === case_.id ? "Flytter..." : "Flytt til Klar til henting"}</span>
                                <ChevronRight size={14} />
                              </button>
                            )}
                          </div>

                          {/* LANGTID KNAPP NEDERST TIL HØYRE */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              moveToLangtidsordre(case_.id);
                            }}
                            className="ml-2 px-3 py-1 bg-yellow-300 text-yellow-900 text-sm font-semibold rounded hover:bg-yellow-400 transition-colors"
                            title="Flytt til langtidsordre"
                          >
                            Langtid
                          </button>
                        </div>
                      </div>
                    );
                  }

                  // Hvis kunden har flere ordre, vis som gruppe
                  const allReady = customerCases.every(c => c.serviceRepUtført && c.utfortDato);
                  const readyCount = customerCases.filter(c => c.serviceRepUtført && c.utfortDato).length;
                  const totalCount = customerCases.length;
                  const hasOldCases = customerCases.some(c => getDaysInService(c) > 7);
                  const hasMediumOldCases = customerCases.some(c => getDaysInService(c) > 3);
                  
                  // Status for delvis ferdig
                  const isPartiallyReady = readyCount > 0 && readyCount < totalCount;
                  
                  return (
                    <div key={customerName} className={`border-2 rounded-lg overflow-hidden ${
                      allReady ? 'border-green-300' :
                      hasOldCases ? 'border-red-300' :
                      hasMediumOldCases ? 'border-yellow-300' :
                      'border-blue-200'
                    }`}>
                      {/* Orange advarselsboks øverst */}
                      {isPartiallyReady && (
                        <div className="bg-orange-100 border-b border-orange-200 py-2 flex justify-center">
                          <div className="px-3 py-1.5 rounded-lg bg-orange-500 text-white font-medium text-sm shadow-md border border-orange-600">
                            <div className="flex items-center space-x-2">
                              <span>⚠️</span>
                              <span>OBS! Bare {readyCount} av {totalCount} ordre er ferdig</span>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      <div className={`px-3 py-2 ${
                        allReady ? 'bg-green-50' :
                        hasOldCases ? 'bg-red-50' :
                        hasMediumOldCases ? 'bg-yellow-50' :
                        'bg-blue-50'
                      }`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-2">
                            <User className={
                              allReady ? 'text-green-600' :
                              hasOldCases ? 'text-red-600' :
                              hasMediumOldCases ? 'text-yellow-600' :
                              'text-blue-600'
                            } size={18} />
                            <h3 className="text-base font-semibold text-gray-900">{customerName}</h3>
                            <span className={`px-2 py-0.5 text-xs rounded-full ${
                              allReady ? 'bg-green-200 text-green-800' :
                              hasOldCases ? 'bg-red-200 text-red-800' :
                              hasMediumOldCases ? 'bg-yellow-200 text-yellow-800' :
                              'bg-blue-200 text-blue-800'
                            }`}>
                              {customerCases.length} ordre
                            </span>
                          </div>
                          {allReady && (
                            <span className="text-xs text-green-700 font-medium">Alle ferdig</span>
                          )}
                        </div>
                      </div>
                      
                      <div className="divide-y">
                        {customerCases.map((case_) => {
                          const isReady = case_.serviceRepUtført && case_.utfortDato;
                          return (
                            <div
                              key={case_.id}
                              className={`px-3 py-2 ${
                                isReady ? 'bg-green-50' : 'bg-white'
                              }`}
                            >
                              {/* ØVERSTE LINJE for grouped items */}
                              <div className="flex items-start justify-between">
                                <div 
                                  className="flex-1 cursor-pointer hover:bg-gray-50 rounded p-1 -m-1"
                                  onClick={() => editCase(case_)}
                                >
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center space-x-2">
                                      <span className="text-xs text-gray-500">#{case_.id}</span>
                                      <span className="text-sm font-medium">{case_.maskinMerke || "Maskin"}</span>
                                    </div>
                                  </div>
                                  <div className="text-xs text-gray-600 mt-1 line-clamp-1">
                                    {case_.beskrivelse}
                                  </div>
                                  {case_.kommentar && (
                                    <div className="text-xs text-gray-600 mt-1 p-2 bg-blue-50 border border-blue-200 rounded">
                                     <strong>Kommentar:</strong> {case_.kommentar}
                                    </div>
                                  )}
                                  {isReady && (
                                    <div className="text-xs text-green-700 mt-1">
                                      ✅ {case_.serviceRepUtført} - {case_.utfortDato}
                                    </div>
                                  )}
                                </div>
                                
                                {/* FERDIG CHECKBOX ØVERST TIL HØYRE for grouped items */}
                                <div className="flex items-center space-x-2 ml-2">
                                  <label className="flex items-center space-x-1 cursor-pointer">
                                    <input
                                      type="checkbox"
                                      checked={isReady}
                                      onChange={(e) => handleServiceCompleted(case_.id, e.target.checked)}
                                      disabled={isUpdating === case_.id}
                                      className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                                    />
                                    <span className="text-xs text-gray-700">Ferdig</span>
                                  </label>
                                </div>
                              </div>
                              
                              {/* NEDERSTE LINJE for grouped items */}
                              <div className="flex items-center justify-between mt-2">
                                {/* Klar til henting knapp - kun hvis ferdig */}
                                <div className="flex-1">
                                  {isReady && (
                                    <button
                                      onClick={() => handleMoveToKlarTilHenting(case_)}
                                      disabled={isMoving === case_.id}
                                      className={`flex items-center justify-center space-x-1 px-2 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700 ${
                                        isMoving === case_.id ? "opacity-50 cursor-not-allowed" : ""
                                      }`}
                                    >
                                      <span>{isMoving === case_.id ? "Flytter..." : "Til henting"}</span>
                                      <ChevronRight size={12} />
                                    </button>
                                  )}
                                </div>

                                {/* LANGTID KNAPP NEDERST TIL HØYRE for grouped items */}
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    moveToLangtidsordre(case_.id);
                                  }}
                                  className="ml-2 px-1 py-0.5 bg-yellow-100 text-yellow-800 text-xs rounded hover:bg-yellow-200 transition-colors"
                                  title="Flytt til langtidsordre"
                                >
                                  Langtid
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* SIDEBAR - LANGTIDSORDRE (fast bredde til høyre) */}
      <div className="w-80 flex-shrink-0">
        <div className="bg-yellow-50 rounded-lg shadow-sm border h-fit">
          {/* Header */}
          <div className="p-3 border-b bg-gradient-to-r from-yellow-100 to-amber-100">
            <div className="flex items-center space-x-2 mb-1">
              <Clock className="text-yellow-600" size={18} />
              <h3 className="font-semibold text-gray-900 text-sm">Langtidsordre</h3>
            </div>
            <p className="text-xs text-gray-600">Venter på deler</p>
            <span className="inline-block mt-1 px-2 py-1 text-xs font-medium rounded-full bg-yellow-200 text-yellow-800">
              {langtidsOrdre.length} ordre
            </span>
          </div>

          {/* Liste */}
          <div className="p-2 min-h-[36rem]">
            {langtidsOrdre.length === 0 ? (
              <div className="text-center py-6">
                <Clock className="mx-auto text-yellow-400 mb-2" size={24} />
                <p className="text-xs text-gray-600">Ingen langtidsordre</p>
              </div>
            ) : (
              <div className="space-y-2">
                {langtidsOrdre.map((case_) => (
                  <div
                    key={case_.id}
                    onClick={() => openCaseForEditing(case_)}
                    className="bg-white border border-yellow-200 rounded-lg p-2 hover:shadow-md transition-all cursor-pointer"
                  >
                    <p className="font-medium text-gray-900 text-sm truncate">
                      {case_.kunde}
                    </p>
                    <p className="text-xs text-gray-600 truncate mb-1">
                      {case_.maskinMerke}
                    </p>
                    <p className="text-xs text-gray-500 line-clamp-2 mb-2">
                      {case_.beskrivelse}
                    </p>
                    {case_.kommentar && (
                      <div className="text-xs text-gray-600 bg-yellow-50 border border-yellow-200 rounded p-1.5 mb-2">
                        <strong>Kommentar:</strong> {case_.kommentar}
                      </div>
                    )}
                    <div className="flex items-center justify-between">
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs bg-yellow-100 text-yellow-800">
                        <Calendar size={10} />
                        {getDaysInService(case_)} dager
                      </span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          moveBackToActive(case_.id);
                        }}
                        className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded hover:bg-green-200 transition-colors"
                        title="Flytt tilbake til aktive ordre"
                      >
                        Tilbake
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* SMS Dialog Modal */}
      {showSmsDialog && selectedCaseForMove && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="bg-green-50 border-b border-green-200 px-6 py-4 rounded-t-lg">
              <div className="flex justify-between items-center">
                <div className="flex items-center space-x-2">
                  <MessageSquare className="text-green-600" size={24} />
                  <h3 className="text-lg font-semibold text-gray-900">Send melding til kunde</h3>
                </div>
                <button
                  onClick={() => {
                    setShowSmsDialog(false);
                    setSelectedCaseForMove(null);
                  }}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X size={20} />
                </button>
              </div>
            </div>

            <div className="p-6">
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                <div className="flex items-start space-x-2">
                  <Phone className="text-yellow-600 mt-0.5" size={20} />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-yellow-800 mb-1">
                      ⚠️ HUSK Å SENDE MELDING
                    </p>
                    <p className="text-sm text-yellow-700">
                      Send melding til kunden om at maskinen er klar for henting
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Kunde:</span>
                      <span className="font-medium text-gray-900">{selectedCaseForMove.kunde}</span>
                    </div>
                    
                    <div className="flex items-center justify-between border-t pt-3">
                      <span className="text-sm text-gray-600 flex items-center space-x-1">
                        <Phone size={16} />
                        <span>Telefon:</span>
                      </span>
                      <span className="text-2xl font-bold text-green-600 bg-white px-4 py-2 rounded border border-green-300">
                        {formatPhoneNumber(selectedCaseForMove.tlf) || "Ikke registrert"}
                      </span>
                    </div>
                    
                    <div className="flex items-center justify-between border-t pt-3">
                      <span className="text-sm text-gray-600">Maskin:</span>
                      <span className="font-medium text-gray-900">
                        {selectedCaseForMove.maskinMerke || "Ikke spesifisert"}
                      </span>
                    </div>

                    {selectedCaseForMove.serienr && (
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Serienr:</span>
                        <span className="text-sm text-gray-700">{selectedCaseForMove.serienr}</span>
                      </div>
                    )}
                  </div>
                </div>

                {selectedCaseForMove.tlf && (
                  <div className="text-center">
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(selectedCaseForMove.tlf);
                        alert("Telefonnummer kopiert!");
                      }}
                      className="text-sm text-blue-600 hover:text-blue-700 underline"
                    >
                      Kopier telefonnummer
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div className="px-6 py-4 bg-gray-50 border-t flex justify-between rounded-b-lg">
              <button
                onClick={() => {
                  setShowSmsDialog(false);
                  setSelectedCaseForMove(null);
                }}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
              >
                Avbryt
              </button>
              <button
                onClick={confirmMoveToKlarTilHenting}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center space-x-2"
              >
                <Check size={18} />
                <span>Flytt til Klar til henting</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Service;